import React, { useMemo, useState } from "react";
import { Check, ChevronDown, X, Star } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Generic multi-select with checkbox list.
 * Props:
 *  - options:  [{ value, label, sublabel? }]
 *  - values:   string[]  (ordered; index 0 is "primary")
 *  - onChange: (next: string[]) => void
 *  - placeholder, primaryLabel (e.g. "Primary"), emptyText, testid, disabled, className
 *  - showPrimaryBadge (default true): shows a star badge on the first chip
 */
export function MultiSelect({
  options = [],
  values = [],
  onChange,
  placeholder = "Select…",
  primaryLabel = "Primary",
  emptyText = "No options",
  testid = "multi-select",
  disabled = false,
  showPrimaryBadge = true,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const byValue = useMemo(() => {
    const m = new Map();
    options.forEach((o) => m.set(o.value, o));
    return m;
  }, [options]);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter(
      (o) =>
        (o.label || "").toLowerCase().includes(q) ||
        (o.sublabel || "").toLowerCase().includes(q)
    );
  }, [options, query]);

  const toggle = (val) => {
    if (disabled) return;
    if (values.includes(val)) {
      onChange(values.filter((v) => v !== val));
    } else {
      onChange([...values, val]);
    }
  };

  const remove = (val, e) => {
    e?.stopPropagation();
    onChange(values.filter((v) => v !== val));
  };

  const makePrimary = (val, e) => {
    e?.stopPropagation();
    if (values[0] === val) return;
    const rest = values.filter((v) => v !== val);
    onChange([val, ...rest]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          className={cn(
            "w-full min-h-10 flex items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm",
            "ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <div className="flex flex-wrap gap-1.5 flex-1 min-w-0 text-left">
            {values.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              values.map((v, idx) => {
                const opt = byValue.get(v);
                if (!opt) return null;
                const isPrimary = idx === 0;
                return (
                  <span
                    key={v}
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
                      isPrimary
                        ? "bg-[#0B7E8C]/10 text-[#0B7E8C] border border-[#0B7E8C]/30"
                        : "bg-slate-100 text-slate-700 border border-slate-200"
                    )}
                    data-testid={`${testid}-chip-${v}`}
                  >
                    {showPrimaryBadge && isPrimary && (
                      <Star className="w-3 h-3 fill-[#0B7E8C] text-[#0B7E8C]" />
                    )}
                    <span className="truncate max-w-[160px]">{opt.label}</span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => remove(v, e)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") remove(v, e);
                      }}
                      className="hover:text-red-600 rounded p-0.5 -mr-0.5 cursor-pointer"
                      data-testid={`${testid}-chip-remove-${v}`}
                      aria-label={`Remove ${opt.label}`}
                    >
                      <X className="w-3 h-3" />
                    </span>
                  </span>
                );
              })
            )}
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        data-testid={`${testid}-popover`}
      >
        <div className="p-2 border-b border-slate-100">
          <input
            type="text"
            placeholder="Search…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 focus:outline-none focus:ring-1 focus:ring-[#0B7E8C]"
            data-testid={`${testid}-search`}
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-slate-500">{emptyText}</div>
          ) : (
            filtered.map((o) => {
              const checked = values.includes(o.value);
              const isPrimary = values[0] === o.value;
              return (
                <div
                  key={o.value}
                  onClick={() => toggle(o.value)}
                  className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-slate-50 cursor-pointer text-sm"
                  data-testid={`${testid}-option-${o.value}`}
                >
                  <span
                    className={cn(
                      "w-4 h-4 rounded border flex items-center justify-center shrink-0",
                      checked ? "bg-[#0B7E8C] border-[#0B7E8C]" : "border-slate-300"
                    )}
                  >
                    {checked && <Check className="w-3 h-3 text-white" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-slate-900">{o.label}</div>
                    {o.sublabel && (
                      <div className="truncate text-[11px] text-slate-500">{o.sublabel}</div>
                    )}
                  </div>
                  {checked && values.length > 1 && (
                    <button
                      type="button"
                      onClick={(e) => makePrimary(o.value, e)}
                      className={cn(
                        "text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded",
                        isPrimary
                          ? "bg-[#0B7E8C]/15 text-[#0B7E8C]"
                          : "text-slate-500 hover:bg-slate-100"
                      )}
                      title={isPrimary ? `${primaryLabel}` : `Make ${primaryLabel}`}
                      data-testid={`${testid}-primary-${o.value}`}
                    >
                      {isPrimary ? `★ ${primaryLabel}` : `Set ${primaryLabel}`}
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
        {values.length > 0 && (
          <div className="border-t border-slate-100 px-2.5 py-1.5 flex items-center justify-between text-[11px] text-slate-500">
            <span>
              {values.length} selected · first is {primaryLabel.toLowerCase()}
            </span>
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-slate-500 hover:text-red-600 font-medium"
              data-testid={`${testid}-clear`}
            >
              Clear
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default MultiSelect;
